import { useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { LOGAN_PICKUP as C } from "@/data/loganPickupContent";
import {
  Plane, Phone, ChevronRight, ChevronDown, Luggage, Signpost, Bus, Car,
  MapPin, AlertTriangle, Smartphone, Clock, IdCard, MessagesSquare, CircleCheck,
  ArrowRight, Info, PlaneLanding, PlaneTakeoff,
} from "lucide-react";

const ICONS = {
  luggage: Luggage, signpost: Signpost, bus: Bus, car: Car,
  smartphone: Smartphone, clock: Clock, idcard: IdCard, messages: MessagesSquare, mappin: MapPin,
};

const SITE = "https://belorentals.com";

// Contextual internal links woven into the body copy.
const IL = ({ to, children }) => (
  <Link to={to} className="text-blue-600 font-medium hover:underline underline-offset-2">{children}</Link>
);

export default function LoganPickupDirections() {
  const [tab, setTab] = useState("A");
  const [openFaq, setOpenFaq] = useState(0);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: C.breadcrumb.map((b, i) => ({
      "@type": "ListItem", position: i + 1, name: b.name, item: `${SITE}${b.to}`,
    })),
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: C.faqs.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const active = C.terminals.items.find((t) => t.key === tab) || C.terminals.items[0];

  return (
    <div data-testid="logan-pickup-directions" className="bg-white">
      <Seo
        title={C.meta.title}
        description={C.meta.description}
        path={C.meta.path}
        keywords={C.meta.keywords}
        type="article"
        jsonLd={[breadcrumbLd, faqLd]}
      />

      {/* Hero + breadcrumb + above-the-fold CTA */}
      <section className="relative pt-28 pb-14 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
              {C.breadcrumb.map((b, i) => (
                <li key={b.to} className="flex items-center gap-1.5">
                  {i < C.breadcrumb.length - 1 ? (
                    <Link to={b.to} className="hover:text-blue-600 transition-colors">{b.name}</Link>
                  ) : (
                    <span className="text-slate-800 font-medium" aria-current="page">{b.name}</span>
                  )}
                  {i < C.breadcrumb.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-6 text-center">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-5">
              <Plane size={15} /> {C.hero.badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{C.hero.h1}</h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{C.hero.intro}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link data-testid="pickup-cta-book" to="/logan-airport-car-rental" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors">
                Book your Logan Airport car <ArrowRight size={17} />
              </Link>
              <a href="tel:+19782211451" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-slate-300 text-slate-700 hover:border-blue-400 hover:text-blue-700 font-semibold transition-colors">
                <Phone size={17} /> {C.hero.phoneLabel}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Four-step overview */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-900">{C.fourStep.heading}</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">{C.fourStep.subheading}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {C.fourStep.steps.map((s, i) => {
            const Icon = ICONS[s.icon] || Info;
            return (
              <div key={i} data-testid={`fourstep-${i}`} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><Icon size={20} /></span>
                <div className="text-xs font-bold text-blue-600 mb-1">STEP {i + 1}</div>
                <h3 className="font-heading font-bold text-slate-900">{s.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Location clarification */}
      <section className="px-5 sm:px-8 max-w-4xl mx-auto">
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/60 p-6 sm:p-7 flex gap-4" data-testid="location-note">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-amber-400 text-white shrink-0"><AlertTriangle size={20} /></span>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{C.locationNote.heading}</h2>
            <p className="text-slate-700 text-sm mt-2 leading-relaxed">{C.locationNote.body}</p>
          </div>
        </div>
      </section>

      {/* Terminal tabs */}
      <section className="py-16 px-5 sm:px-8 max-w-4xl mx-auto">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-900">{C.terminals.heading}</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">{C.terminals.subheading}</p>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-2" role="tablist" aria-label="Terminal directions">
          {C.terminals.items.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`terminal-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-6 bg-slate-50 rounded-2xl border border-slate-100 p-6 sm:p-8" data-testid={`terminal-panel-${active.key}`} role="tabpanel">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-600 text-white"><PlaneLanding size={18} /></span>
            <h3 className="font-heading text-lg font-bold text-slate-900">{active.title}</h3>
          </div>
          <p className="text-slate-700 leading-relaxed">{active.body}</p>
        </div>
        {/* Crawlable: render every terminal's copy in the HTML for SEO, visually only the active one shown above */}
        <div className="sr-only">
          {C.terminals.items.map((t) => (
            <div key={t.key}><h3>{t.title}</h3><p>{t.body}</p></div>
          ))}
        </div>
      </section>

      {/* Timelines */}
      <section className="py-4 px-5 sm:px-8 max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
        {[
          { data: C.pickupTimeline, icon: PlaneLanding, testid: "pickup-timeline", accent: "bg-blue-600" },
          { data: C.returnTimeline, icon: PlaneTakeoff, testid: "return-timeline", accent: "bg-slate-800" },
        ].map(({ data, icon: TIcon, testid, accent }) => (
          <div key={testid} data-testid={testid}>
            <div className="flex items-center gap-2.5 mb-2">
              <span className={`grid place-items-center w-9 h-9 rounded-lg ${accent} text-white`}><TIcon size={18} /></span>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{data.heading}</h2>
            </div>
            <p className="text-slate-500 text-sm mb-6">{data.subheading}</p>
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-6">
              {data.steps.map((s, i) => (
                <li key={i} className="pl-6 relative">
                  <span className={`absolute -left-[11px] top-0.5 grid place-items-center w-5 h-5 rounded-full ${accent} text-white text-[10px] font-bold`}>{i + 1}</span>
                  <h3 className="font-semibold text-slate-900 text-sm">{s.title}</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </section>

      {/* Map */}
      <section className="py-16 px-5 sm:px-8 max-w-5xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">{C.map.heading}</h2>
        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 shadow-card">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              title="Boston Logan Economy Parking Garage — car-share pickup area map"
              data-testid="economy-garage-map"
              src={C.map.embedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
            />
          </div>
          <div className="bg-slate-900 text-white px-5 py-3 flex items-center gap-2 text-sm font-medium">
            <MapPin size={16} className="text-blue-300 shrink-0" /> {C.map.caption}
          </div>
        </div>
        <p className="text-slate-500 text-sm mt-4 max-w-2xl mx-auto text-center leading-relaxed">{C.map.note}</p>
      </section>

      {/* Already booked */}
      <section className="px-5 sm:px-8 max-w-4xl mx-auto">
        <div className="rounded-2xl bg-blue-50 border border-blue-100 p-6 sm:p-8 flex gap-4" data-testid="already-booked">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white shrink-0"><CircleCheck size={20} /></span>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{C.alreadyBooked.heading}</h2>
            <p className="text-slate-700 text-sm mt-2 leading-relaxed">{C.alreadyBooked.body}</p>
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="py-16 px-5 sm:px-8 max-w-4xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">{C.commonMistakes.heading}</h2>
        <ul className="mt-8 space-y-3" data-testid="common-mistakes">
          {C.commonMistakes.items.map((m, i) => (
            <li key={i} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4">
              <AlertTriangle size={18} className="text-rose-500 shrink-0 mt-0.5" />
              <span className="text-slate-700 text-sm leading-relaxed">{m}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Travel tips */}
      <section className="py-4 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">{C.travelTips.heading}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="travel-tips">
          {C.travelTips.items.map((t, i) => {
            const Icon = ICONS[t.icon] || Info;
            return (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><Icon size={20} /></span>
                <h3 className="font-heading font-bold text-slate-900">{t.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{t.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contextual internal links prose */}
      <section className="py-16 px-5 sm:px-8 max-w-3xl mx-auto">
        <div className="prose prose-slate text-slate-600 leading-relaxed text-sm sm:text-base">
          <p>
            Planning the rest of your Boston trip? If you're weighing options, our{" "}
            <IL to="/logan-airport-car-rental">Logan Airport car rental</IL> page covers booking and airport handoff details, while{" "}
            <IL to="/economy-car-rental-boston">economy car rental in Boston</IL> is a great fit for solo travelers and couples keeping costs down.
          </p>
          <p className="mt-4">
            Staying a while? Compare{" "}
            <IL to="/boston-weekly-car-rental">weekly car rental</IL> and{" "}
            <IL to="/long-term-car-rental-boston">long-term car rental in Boston</IL> for better value on longer visits. Prefer a fully digital handoff? See{" "}
            <IL to="/contactless-car-rental-boston">contactless car rental in Boston</IL>. And if a New England getaway is on the itinerary, our{" "}
            <IL to="/boston-vacation-car-rental">Boston vacation car rental</IL> and{" "}
            <IL to="/logan-airport-to-salem-car-rental">Logan Airport to Salem</IL> guides can help you plan the drive.
          </p>
        </div>
      </section>

      {/* FAQ accordion */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-3" data-testid="faq-list">
            {C.faqs.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
          {/* Crawlable copy of all answers regardless of accordion state */}
          <div className="sr-only">
            {C.faqs.map((f, i) => (<div key={i}><h3>{f.q}</h3><p>{f.a}</p></div>))}
          </div>
        </div>
      </section>

      {/* Related pages */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">Related Boston &amp; Logan Airport rentals</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="related-links">
          {[
            ["/logan-airport-car-rental", "Logan Airport Car Rental"],
            ["/economy-car-rental-boston", "Economy Car Rental Boston"],
            ["/boston-weekly-car-rental", "Weekly Car Rental Boston"],
            ["/long-term-car-rental-boston", "Long-Term Car Rental Boston"],
            ["/contactless-car-rental-boston", "Contactless Car Rental Boston"],
            ["/boston-vacation-car-rental", "Boston Vacation Car Rental"],
            ["/logan-airport-to-salem-car-rental", "Logan → Salem"],
          ].map(([to, label]) => (
            <Link key={to} to={to} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">{C.finalCta.heading}</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">{C.finalCta.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link data-testid="pickup-final-cta" to="/logan-airport-car-rental" className="px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Book your car</Link>
            <a href="tel:+19782211451" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">
              <Phone size={17} /> {C.hero.phoneLabel}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
